import React, { memo, useRef, useState, useEffect } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddBoxIcon from "@material-ui/icons/AddBox";
import { Alert } from '@material-ui/lab';
import {
    Box,
    Button,
    Chip,
    Divider,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Grid,
    makeStyles,
    TextField,
    Typography
} from "@material-ui/core";
import theme from '../../styles/theme';
import useStyles from '../../styles/styles';
import useTrafficStyles from "../../styles/trafficStyles";
import { urlFields, paramFields } from '../../utils/OfferMap';
import ExpanderList from '../Common/ExpanderList';
import _ from "lodash";
import PanelBox from '../TrafficSource/PanelBox';
import setActiveIcon from '../../utils/setActiveIcon';

const useExpanderListStyle = makeStyles({
    root: {
        border: '1px solid',

        '& .MuiExpansionPanelSummary-root': {
            padding: theme.spacing(0, 2, 0, 1),
        },
    },
    default: {
        display: 'block'
    },
    removeButton: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
});

const OfferURLTable = memo(({ 
    data,
    urls,
    editView,
    inputs,
    setInputs,
    handleFilterChange,
    handleSwitchChange,
    urlInputs,
    setUrlInputs,
    setDefaultUrlInputs,
    hasTrafficSourceByOffers
}) => {
    const classes = useStyles();
    const expandClasses = useExpanderListStyle();
    const trafficClasses = useTrafficStyles();
    const [expandedPanelsArr, setExpandedPanelsArr] = useState([]);
    const [sourceData, setSourceData] = useState([]);
    const [showInactive, setShowInactive] = useState(editView);

    const setData = (objData) => {
        objData.forEach(url => {
            let newUrlObj = {};
            const { offerUrlQueryParameters } = url;
            newUrlObj = {
                id: url.offerUrlId || '',
                httpMethod: 'GET',
                offerId: url.offerId || '',
                activateTrafficSourceOfferUrls: hasTrafficSourceByOffers
            };
            // URL Fields
            urlFields.forEach(section => {
                section.arr.forEach(field => {
                    const {key, type} = field;
                    if (inputs[key] === undefined) {
                        const dataKey = url[key];
                        let value = dataKey === '' ? '' : dataKey;

                        if (type === 'switch' && value === '') {
                            value = false;
                        }
                        
                        newUrlObj = {
                            ...newUrlObj,
                            [key]: value
                        };
                    }
                });
            });

            newUrlObj.offerUrlQueryParameters = [];
            if (offerUrlQueryParameters.length) {
                offerUrlQueryParameters.forEach(query => {
                    // Param Fields
                    let obj = {};
                    let arr = [];
                    paramFields.forEach(paramField => {
                        obj = {
                            ...paramField,
                            value: query[paramField.key]
                        }
                        arr.push(obj);
                    });
                    arr.push({
                        offerUrlQueryParameterId: query.offerUrlQueryParameterId,
                        key: 'offerUrlQueryParameterId'
                    }, {
                        offerUrlId: query.offerUrlId,
                        key: 'offerUrlId'
                    }, {
                        key: 'deleteFlag',
                        delete: false
                    });
                    newUrlObj.offerUrlQueryParameters.push(arr);
                });
            }
            setUrlInputs(urlInputs => [...urlInputs, newUrlObj]);
            setDefaultUrlInputs(urlInputs => [...urlInputs, newUrlObj]);
        });
    };

    useEffect(() => {
        if (data && data.allSourceData) {
            let arr = [];
            arr = data.allSourceData.enumValues.map(item => {
                return item.name.toLowerCase();
            })
            setSourceData(arr);
        }
    }, []);

    useEffect(() => {
        if (urls.length) {
            setData(urls);
        }
    }, [urls]);

    const handleKey = (e) => {
        if (e.key === 'Enter') {
            e.stopPropagation()
        }
    }

    const addURLRow = () => {
        let values = [...urlInputs];
        let newSet = {
            httpMethod: "GET",
            offerUrlQueryParameters: [],
            activateTrafficSourceOfferUrls: hasTrafficSourceByOffers
        };
        urlFields.forEach(section => {
            section.arr.forEach(field => {
                newSet = {
                    ...newSet,
                   [field.key]: field.type === 'switch' ? true : ''
                };
            });
        });

        newSet.offerUrlQueryParameters = [];
        values.push(newSet);
        setUrlInputs(values);
    };

    const handleQueryParametersChange = (idx, rowIdx, newVal, id) => {
        let arr = [...urlInputs];
        let obj = arr[id];
        let params = obj.offerUrlQueryParameters[idx];
        let paramVal = params[rowIdx];
        paramVal = {
            ...paramVal,
            value: newVal
        };
        params[rowIdx] = paramVal;
        obj.offerUrlQueryParameters[idx] = params;
        arr[id] = obj;
        setUrlInputs(arr);
    }

    const addQueryParametersRow = (idx) => {
        let arr = [...urlInputs];
        let obj = arr[idx].offerUrlQueryParameters;
        const newSet = paramFields.map(item => {
            return {
                ...item,
                value: ''
            };
        });
        obj.push(newSet);
        arr[idx].offerUrlQueryParameters = obj;
        setUrlInputs(arr);
    };

    const deleteQueryParametersRow = (deleteIdx, deleteUrlIdx) => {
        let arr = [...urlInputs];
        let obj = arr[deleteUrlIdx].offerUrlQueryParameters;
        const newSet = obj[deleteIdx].map(item => {
            if (item.key === 'deleteFlag') {
                item.delete = true;
            }
            return {
                ...item,
            };
        });
        arr[deleteUrlIdx].offerUrlQueryParameters[deleteIdx] = [...newSet];
        setUrlInputs(arr);
    };

    const getUrlInfo = (search) => {
        const url = new URL(search)
        const params = new URLSearchParams(url.search)

        return {
            baseUrl: `${url.protocol}//${url.hostname}${url.pathname}`,
            params
        };
    }

    const handleUrlInputChange = (event, urlIdx) => {
        event.persist();
        if (event) {
            const { name, value, checked } = event.target;
            let newVal = value || '';
            if (name === 'active' || name === 'default' || name === 'forwardQueryParams') {
                newVal = checked;
            }

            let values = [...urlInputs];
            let obj = values[urlIdx];
            obj = {
                ...obj,
                [name]: newVal
            };
            
            values[urlIdx] = obj;
            setUrlInputs(values);
        }   
    };

    const handleUrlDeconstruct = (urlIdx) => {
        let values = [...urlInputs];
        let obj = values[urlIdx];

        const urlObj = getUrlInfo(obj.deconstructUrlParameters);
        const { baseUrl, params } = urlObj;
        const { offerUrlQueryParameters } = obj;

        obj.url = baseUrl;
        if (Object.keys(params).length) {
            let paramObj = {};
            Object.entries(params).forEach(key => {
                const mainKey = key[0];
                const keyValue = key[1];
                const newKey = keyValue.replace(/[{}]/g, "");
                const isSource = sourceData.filter(item => item === newKey.toLowerCase());
                let newSet = paramFields.map(item => {
                    let newValue = '';
                    
                    if (isSource.length) {  
                        if (item.key === 'parameterName') {
                            newValue = mainKey;
                        }

                        if (item.key === 'sourceData') {
                            newValue = newKey.toUpperCase();
                        }
                    } else {
                        if (item.key === 'constantValue') {
                            newValue = newKey;
                        }

                        if (item.key === 'parameterName') {
                            newValue = mainKey;
                        }

                        if (item.key === 'sourceData') {
                            newValue = 'CONSTANT';
                        }
                    }

                    return {
                        ...item,
                        value: newValue
                    };
                });
                offerUrlQueryParameters.push(newSet);
            });
        }
        values[urlIdx] = obj;
        setUrlInputs(values);
    };

    const inActive = urlInputs.filter(item => !item.active);

    return (
        <>
            <Box display="flex" justifyContent={"space-between"} style={{ marginBottom: '5px' }}>
                <Typography variant="body1" align="left" gutterBottom>
                    Build Offer URLs below.
                    {inActive.length && !editView ? (
                        <Button
                            variant="primary"
                            className={classes.addButton}
                            onClick={() => setShowInactive(!showInactive)}
                            size="small"
                            style={{ margin: '0 10px' }}
                        >
                            {!showInactive ? 'View' : 'Hide'} Inactive ({inActive.length})
                        </Button>
                    ) : null}
                </Typography>
                {editView ? (
                    <Button
                        variant="contained"
                        className={classes.addButton}
                        onClick={() => addURLRow()}
                        size="small"
                    >
                        Add URL <AddBoxIcon/>
                    </Button>
                ) : null}
            </Box>
            {!urlInputs.length ? (
                <Alert severity="warning">
                    There are no Offer URLs at this time.
                </Alert>
            ) : (
               <>
                   {urlInputs.map((item, urlIdx) => {
                        const isExpanded = expandedPanelsArr.length ? expandedPanelsArr.includes(urlIdx) : false;
                        const { active, name, url, offerUrlQueryParameters } = item;
                        const showOnlyActive = active && !editView || editView;
                        if (showOnlyActive || showInactive) {
                            return (
                                <ExpansionPanel
                                    key={urlIdx.toString()}
                                    id={`${name}-${urlIdx}`}
                                    expanded={isExpanded}
                                    className={expandClasses.root}
                                    onChange={(e, bool) => {
                                        if (bool) {
                                            setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, urlIdx]);
                                        } else {
                                            if (expandedPanelsArr.includes(urlIdx)) {
                                                setExpandedPanelsArr(expandedPanelsArr.filter(panel => panel !== urlIdx));
                                            }
                                        }
                                    }}
                                >
                                    <ExpansionPanelSummary
                                        expandIcon={<ExpandMoreIcon/>}
                                    >
                                        <Grid
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={3}
                                        >
                                            <Grid container key={urlIdx.toString()} style={{ margin: '20px'}}  alignItems="center">
                                                <Grid item xs={12} md={1}>
                                                    <Box display="flex" flexDirection="row" alignItems="center">
                                                        <Box textAlign="left">
                                                            {setActiveIcon(active)}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Box display="flex" flexDirection="row" alignItems="center">
                                                        <Box textAlign="left">
                                                            <Typography variant="body1">
                                                                <strong>
                                                                    {isExpanded ? `Close ${name}` : `${editView ? 'Edit' : 'View'} ${name}`}
                                                                </strong>
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                {!isExpanded ? (
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1" style={{ paddingBottom: '5px' }}>
                                                            <strong>{url}</strong>
                                                        </Typography>
                                                    </Grid>
                                                ) : null}
                                            </Grid>
                                        </Grid>
                                    </ExpansionPanelSummary>
                                    <ExpansionPanelDetails className={classes.details}>
                                        <Grid
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={3}
                                        >
                                            {editView ? (
                                                <Grid item xs={12}>
                                                    <Box className={`${classes.filterBox} ${trafficClasses.panelBox}`} style={{marginTop: '10px'}}>
                                                        <Chip
                                                            label={'Deconstruct Url Parameters'}
                                                            className={classes.filterSectionLabels}
                                                        />
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={9}>
                                                                <TextField
                                                                    id={'deconstructUrlParameters'}
                                                                    name={'deconstructUrlParameters'}
                                                                    label={'Url to Deconstruct'}
                                                                    value={urlInputs[urlIdx].deconstructUrlParameters || ''}
                                                                    type={'text'}
                                                                    onChange={e => handleUrlInputChange(e, urlIdx)}
                                                                    fullWidth
                                                                    disabled={false}
                                                                    readOnly={false}
                                                                    onKeyDown={e => handleKey(e)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Button variant="contained" color="primary" onClick={() => handleUrlDeconstruct(urlIdx)}>
                                                                    Deconstruct
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                    <Divider style={{margin: '30px 0'}}/>
                                                </Grid>
                                            ) : null}
                                            <Grid item xs={12}>
                                                <Box className={trafficClasses.headerBox}>
                                                    <Grid container spacing={3} alignItems="flex-end">
                                                        {urlFields.map((section, idx) => {
                                                            return (
                                                                <PanelBox
                                                                    key={`${idx.toString()} `}
                                                                    panelData={section}
                                                                    trafficClasses={trafficClasses}
                                                                    editView={editView}
                                                                    inputs={item}
                                                                    data={data}
                                                                    handleFilterChange={handleFilterChange}
                                                                    handleInputChange={(e) => handleUrlInputChange(e, urlIdx)}
                                                                    handleSwitchChange={(e) => handleUrlInputChange(e, urlIdx)}
                                                                    setInputs={setInputs}
                                                                    rowCol={12}
                                                                />
                                                            );
                                                        })}
                                                    </Grid>
                                                </Box>
                                                <Divider style={{margin: '30px 0'}}/>
                                                <ExpanderList
                                                    idPrefix={urlIdx}
                                                    listType={'Params'}
                                                    editView={editView}
                                                    fieldData={offerUrlQueryParameters}
                                                    graphqlData={data}
                                                    inputs={item}
                                                    setInputs={setInputs}
                                                    showDefaultStyles={false}
                                                    addRow={addQueryParametersRow}
                                                    deleteRow={deleteQueryParametersRow}
                                                    handleFieldChange={handleQueryParametersChange}
                                                    handleSwitchChange={handleSwitchChange}
                                                />
                                            </Grid>
                                        </Grid>
                                    </ExpansionPanelDetails>
                                </ExpansionPanel>
                            );
                        }
                    })}
                </>
            )}  
        </>
    )
});

export default OfferURLTable;